import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axiosConfig';
import '../pages/Profiles.css';

const EditarTramiteModal = ({ isOpen, tramiteId, onClose, onSuccess }) => {
  const [transitos, setTransitos] = useState([]);
  const [tramiteTypes, setTramiteTypes] = useState([]);
  const [plates, setPlates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPlateDropdown, setShowPlateDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [searchingOwner, setSearchingOwner] = useState(false);
  const [ownerChecked, setOwnerChecked] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [existingFiles, setExistingFiles] = useState([]);

  const [formData, setFormData] = useState({
    transito_id: '',
    tipo_tramite_id: '',
    vehicle_type: '',
    propietario: '',
    tipo_documento_id: 'CC',
    documento: '',
    correo: '',
    telefono: '',
    direccion: '',
    placa_referencia: '',
    observaciones: '',
    chasis: '',
    motor: '',
    vin: '',
    vehicleDetails: null
  });

  const [files, setFiles] = useState({
    cedula: null,
    factura: null,
    declaracion_importacion: null
  });

  const [transitoConfig, setTransitoConfig] = useState(null);

  const fetchInitialData = useCallback(async () => {
    try {
      const [transRes, typesRes] = await Promise.all([
        axios.get('/api/transitos'),
        axios.get('/api/tramite-types')
      ]);
      setTransitos(transRes.data);
      setTramiteTypes(typesRes.data);
    } catch (err) {
      console.error('Error al cargar datos iniciales:', err);
    }
  }, []);

  const fetchTramiteDetails = useCallback(async () => {
    try {
      setFetchingDetails(true);
      const res = await axios.get(`/api/tramites/${tramiteId}`);
      const data = res.data;
      
      setFormData({
        transito_id: data.transito_id || '',
        tipo_tramite_id: data.tipo_tramite_id || '',
        vehicle_type: data.datos_adicionales?.vehicle_type || '',
        propietario: data.datos_adicionales?.propietario || '',
        tipo_documento_id: data.datos_adicionales?.tipo_documento || 'CC',
        documento: data.datos_adicionales?.documento || '',
        correo: data.datos_adicionales?.correo || '',
        telefono: data.datos_adicionales?.telefono || '',
        direccion: data.datos_adicionales?.direccion || '',
        placa_referencia: data.datos_adicionales?.placa_referencia || '',
        observaciones: data.datos_adicionales?.observaciones || '',
        chasis: data.datos_adicionales?.chasis || '',
        motor: data.datos_adicionales?.motor || '',
        vin: data.datos_adicionales?.vin || '',
        vehicleDetails: data.datos_adicionales?.vehicleDetails || null
      });
      setSearchTerm(data.datos_adicionales?.placa_referencia || '');
      setExistingFiles(data.archivos || []);
      setOwnerChecked(true);
    } catch (err) {
      console.error('Error al cargar detalles del trámite:', err);
      alert('❌ No se pudieron cargar los detalles del trámite');
    } finally {
      setFetchingDetails(false);
    }
  }, [tramiteId]);

  useEffect(() => {
    if (isOpen && tramiteId) {
      fetchInitialData();
      fetchTramiteDetails();
    } else {
      resetForm();
    }
  }, [isOpen, tramiteId, fetchInitialData, fetchTramiteDetails]);

  useEffect(() => {
    if (formData.transito_id) {
      const selectedTransito = transitos.find(t => t.id === parseInt(formData.transito_id));
      setTransitoConfig(selectedTransito);
      
      if (selectedTransito?.plate_assignment_mode === 'automatico') {
        const availablePlatesForType = plates.filter(p => p.vehicle_type === formData.vehicle_type && p.status === 'disponible');
        const nextPlate = availablePlatesForType.length > 0 ? availablePlatesForType[0].plate_number : 'PENDIENTE';
        
        if (formData.vehicle_type) {
          setSearchTerm(`AUTO: ${nextPlate}`);
          setFormData(prev => ({ ...prev, placa_referencia: 'AUTO' }));
        } else {
          setSearchTerm('SELECCIONE TIPO DE VEHÍCULO');
          setFormData(prev => ({ ...prev, placa_referencia: '' }));
        }
      }
    } else {
      setTransitoConfig(null);
    }
  }, [formData.transito_id, formData.vehicle_type, transitos, plates]);

  useEffect(() => {
    const fetchPlates = async () => {
      if (formData.transito_id && formData.vehicle_type) {
        try {
          const res = await axios.get(`/api/transitos/${formData.transito_id}/plates?vehicle_type=${formData.vehicle_type}`);
          setPlates(res.data.filter(p => p.status === 'disponible' || p.id === formData.vehicleDetails?.pre_assigned_plate_id));
        } catch (err) {
          console.error('Error al cargar placas:', err);
          setPlates([]);
        }
      } else {
        setPlates([]);
      }
    };
    fetchPlates();
  }, [formData.transito_id, formData.vehicle_type, formData.vehicleDetails?.pre_assigned_plate_id]);

  const resetForm = () => {
    setFormData({
      transito_id: '',
      tipo_tramite_id: '',
      vehicle_type: '',
      propietario: '',
      tipo_documento_id: 'CC',
      documento: '',
      correo: '',
      telefono: '',
      direccion: '',
      placa_referencia: '',
      observaciones: '',
      chasis: '',
      motor: '',
      vin: '',
      vehicleDetails: null
    });
    setFiles({ cedula: null, factura: null, declaracion_importacion: null });
    setExistingFiles([]);
    setSearchTerm('');
    setOwnerChecked(false);
    setMessage({ text: '', type: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const upperValue = (name !== 'transito_id' && name !== 'tipo_tramite_id' && name !== 'tipo_documento_id' && name !== 'correo' && name !== 'vehicle_type') 
      ? value.toUpperCase() 
      : value;

    setFormData({ ...formData, [name]: upperValue });
    
    if (name === 'transito_id') {
      setFormData(prev => ({ ...prev, transito_id: value, placa_referencia: '', tipo_tramite_id: '' }));
      setSearchTerm('');
    }
    if (name === 'documento' || name === 'tipo_documento_id') {
      setOwnerChecked(false);
      setMessage({ text: '', type: '' });
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 3 * 1024 * 1024) {
      alert(`❌ El archivo ${file.name} supera los 3MB`);
      e.target.value = '';
      return;
    }
    
    setFiles({ ...files, [e.target.name]: file });
  };

  const searchOwner = async () => {
    if (!formData.documento) {
      alert('⚠️ Ingrese un número de documento para buscar');
      return;
    }

    try {
      setSearchingOwner(true);
      setMessage({ text: '', type: '' });
      const res = await axios.get(`/api/tramites/owner/${formData.documento}?tipo_documento_id=${formData.tipo_documento_id}`);
      
      if (res.data) {
        setFormData(prev => ({
          ...prev,
          propietario: res.data.propietario || '',
          tipo_documento_id: res.data.tipo_documento_id || 'CC',
          correo: res.data.correo || '',
          telefono: res.data.telefono || '',
          direccion: res.data.direccion || ''
        }));
        
        const estado = res.data.estado_persona;
        if (estado && (estado.toUpperCase() === 'NO INSCRITO' || estado.toUpperCase() === 'INACTIVO')) {
          alert('⚠️ ATENCIÓN: El ciudadano NO está inscrito en el RUNT. Estado: ' + estado);
        } else {
          setMessage({ text: '✅ Información del cliente cargada correctamente', type: 'success' });
        }
      }
      setOwnerChecked(true);
    } catch (err) {
      console.error('Error al buscar cliente:', err);
      setOwnerChecked(true);
      if (err.response?.status === 404) {
        alert('ℹ️ Cliente no encontrado. Ingrese los datos manualmente.');
      } else {
        alert('❌ Error al buscar información del cliente');
      }
    } finally {
      setSearchingOwner(false);
    }
  };

  const handlePlateSelect = (plateNumber) => {
    setFormData({ ...formData, placa_referencia: plateNumber });
    setSearchTerm(plateNumber);
    setShowPlateDropdown(false);
  };

  const filteredPlates = plates.filter(p => 
    p.plate_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTramiteTypes = tramiteTypes.filter(tt => 
    formData.transito_id && tt.transito_id === parseInt(formData.transito_id)
  );

  const getExistingFile = (type) => {
    return existingFiles.find(f => f.tipo_documento === type);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.vehicle_type) {
      alert('⚠️ Debe seleccionar un tipo de vehículo');
      return;
    }
    
    const totalSize = Object.values(files).reduce((acc, file) => acc + (file?.size || 0), 0);
    if (totalSize > 3 * 1024 * 1024) {
      alert('❌ La suma de los archivos cargados no puede superar los 3MB');
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    const data = new FormData();
    data.append('transito_id', formData.transito_id);
    data.append('tipo_tramite_id', formData.tipo_tramite_id);
    data.append('vehicle_type', formData.vehicle_type);
    data.append('placa_referencia', formData.placa_referencia);
    data.append('datos_adicionales', JSON.stringify({
      propietario: formData.propietario,
      tipo_documento: formData.tipo_documento_id,
      documento: formData.documento,
      correo: formData.correo,
      telefono: formData.telefono,
      direccion: formData.direccion,
      vehicle_type: formData.vehicle_type,
      placa_referencia: formData.placa_referencia,
      observaciones: formData.observaciones,
      chasis: formData.chasis,
      motor: formData.motor,
      vin: formData.vin,
      vehicleDetails: formData.vehicleDetails
    }));

    if (files.cedula) data.append('Cedula', files.cedula);
    if (files.factura) data.append('Factura', files.factura);
    if (files.declaracion_importacion) data.append('Declaracion_Importacion', files.declaracion_importacion);

    try {
      await axios.put(`/api/tramites/${tramiteId}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert('✅ Trámite modificado correctamente');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.message || 'No se pudo actualizar el trámite'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '800px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h3>✏️ Editar Trámite #{tramiteId}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        
        {fetchingDetails ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>Cargando información del trámite...</p>
          </div>
        ) : (
          <>
            {message.text && (
              <div className={`
                ${message.type === 'success' ? 'success-banner' : ''}
                ${message.type === 'error' ? 'error-banner' : ''}
                ${message.type === 'info' ? 'info-banner' : ''}
              `}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-section">
                <div className="form-section-header">
                  <div className="form-section-icon">📋</div>
                  <h4 className="form-section-title">DATOS DEL TRÁMITE</h4>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>ORGANISMO DE TRÁNSITO <span style={{ color: 'red' }}>*</span></label>
                    <select name="transito_id" value={formData.transito_id} onChange={handleInputChange} required>
                      <option value="">seleccione...</option>
                      {transitos.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>TIPO DE TRÁMITE <span style={{ color: 'red' }}>*</span></label>
                    <select name="tipo_tramite_id" value={formData.tipo_tramite_id} onChange={handleInputChange} required disabled={!formData.transito_id}>
                      <option value="">
                        {!formData.transito_id 
                          ? 'seleccione primero un tránsito' 
                          : filteredTramiteTypes.length === 0 
                            ? 'no hay trámites asociados a este tránsito' 
                            : 'seleccione...'}
                      </option>
                      {filteredTramiteTypes.map(tt => <option key={tt.id} value={tt.id}>{tt.name} (${parseFloat(tt.value).toLocaleString('es-CO')})</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>TIPO DE VEHÍCULO <span style={{ color: 'red' }}>*</span></label>
                  <div style={{ display: 'flex', gap: '25px', marginTop: '10px', background: 'var(--secondary-soft)', padding: '12px', borderRadius: '8px', border: '1px solid var(--card-border)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      <input 
                        type="radio" 
                        name="vehicle_type"
                        value="carro"
                        checked={formData.vehicle_type === 'carro'} 
                        onChange={handleInputChange}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      🚗 CARRO
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      <input 
                        type="radio" 
                        name="vehicle_type"
                        value="moto"
                        checked={formData.vehicle_type === 'moto'} 
                        onChange={handleInputChange}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      MOTO
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      <input 
                        type="radio" 
                        name="vehicle_type"
                        value="moto_carro"
                        checked={formData.vehicle_type === 'moto_carro'} 
                        onChange={handleInputChange}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      MOTO CARRO
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-section" style={!ownerChecked ? { borderLeft: '4px solid var(--primary-color)' } : {}}>
                <div className="form-section-header">
                  <div className="form-section-icon">🔍</div>
                  <h4 className="form-section-title">VERIFICACIÓN DE IDENTIDAD</h4>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', marginTop: '12px', gap: '12px' }}>
                  <div className="form-group">
                    <label>TIPO DOC. <span style={{ color: 'red' }}>*</span></label>
                    <select name="tipo_documento_id" value={formData.tipo_documento_id} onChange={handleInputChange} required>
                      <option value="CC">cédula de ciudadanía</option>
                      <option value="NIT">nit</option>
                      <option value="CE">cédula de extranjería</option>
                      <option value="PP">pasaporte</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>NRO DE DOCUMENTO <span style={{ color: 'red' }}>*</span></label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input 
                        type="text" 
                        name="documento" 
                        value={formData.documento} 
                        onChange={handleInputChange} 
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchOwner())}
                        required 
                        placeholder="número de documento" 
                        style={{ flex: 1 }}
                      />
                      <button 
                        type="button" 
                        onClick={searchOwner} 
                        className="btn-action btn-modules"
                        disabled={searchingOwner}
                        style={{ padding: '0 20px', height: '45px', fontWeight: '800' }}
                      >
                        {searchingOwner ? '⏳' : 'CONSULTAR'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {ownerChecked && (
                <>
                  <div className="form-section">
                    <div className="form-section-header">
                      <div className="form-section-icon">👤</div>
                      <h4 className="form-section-title">INFORMACIÓN DEL PROPIETARIO</h4>
                    </div>
                    
                    <div className="form-group">
                      <label>NOMBRE COMPLETO <span style={{ color: 'red' }}>*</span></label>
                      <input type="text" name="propietario" value={formData.propietario} onChange={handleInputChange} required placeholder="nombre del propietario" />
                    </div>

                    <div className="form-grid">
                      <div className="form-group">
                        <label>CORREO ELECTRÓNICO</label>
                        <input type="email" name="correo" value={formData.correo} onChange={handleInputChange} placeholder="ejemplo@correo.com" />
                      </div>
                      <div className="form-group">
                        <label>TELÉFONO</label>
                        <input type="text" name="telefono" value={formData.telefono} onChange={handleInputChange} placeholder="número de contacto" />
                      </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: '0' }}>
                      <label>DIRECCIÓN</label>
                      <input type="text" name="direccion" value={formData.direccion} onChange={handleInputChange} placeholder="dirección de residencia" />
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-section-header">
                      <div className="form-section-icon">📂</div>
                      <h4 className="form-section-title">DOCUMENTACIÓN DE SOPORTE</h4>
                    </div>
                    
                    <div className="form-grid">
                      <div className="form-group">
                        <label>CÉDULA (PDF/IMG)</label>
                        {getExistingFile('Cedula') && (
                          <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '5px' }}>
                            ✓ Archivo actual: <em>{getExistingFile('Cedula').nombre_archivo}</em>
                          </div>
                        )}
                        <input type="file" name="cedula" onChange={handleFileChange} className="file-input" accept=".pdf,image/*" />
                        <small style={{ color: '#64748b' }}>Deje en blanco si no desea reemplazar el archivo</small>
                      </div>
                      <div className="form-group">
                        <label>FACTURA (PDF/IMG)</label>
                        {getExistingFile('Factura') && (
                          <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '5px' }}>
                            ✓ Archivo actual: <em>{getExistingFile('Factura').nombre_archivo}</em>
                          </div>
                        )}
                        <input type="file" name="factura" onChange={handleFileChange} className="file-input" accept=".pdf,image/*" />
                        <small style={{ color: '#64748b' }}>Deje en blanco si no desea reemplazar el archivo</small>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>DECLARACIÓN DE IMPORTACIÓN / MANIFIESTO (PDF/IMG)</label>
                      {getExistingFile('Declaracion_Importacion') && (
                        <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '5px' }}>
                          ✓ Archivo actual: <em>{getExistingFile('Declaracion_Importacion').nombre_archivo}</em>
                        </div>
                      )}
                      <input type="file" name="declaracion_importacion" onChange={handleFileChange} className="file-input" accept=".pdf,image/*" />
                      <small style={{ color: '#64748b' }}>Deje en blanco si no desea reemplazar el archivo</small>
                    </div>

                    <div className="form-group" style={{ marginBottom: '0' }}>
                      <label>OBSERVACIONES</label>
                      <textarea name="observaciones" value={formData.observaciones} onChange={handleInputChange} rows="2" placeholder="detalles adicionales..." />
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-section-header">
                      <div className="form-section-icon">🚗</div>
                      <h4 className="form-section-title">DETALLES DEL VEHÍCULO</h4>
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: '0' }}>
                      <label>PLACA DE REFERENCIA {transitoConfig?.plate_assignment_mode === 'automatico' ? '(AUTOMÁTICA)' : '(MANUAL)'}</label>
                      <div className="searchable-select-container">
                        <input
                          type="text"
                          name="placa_referencia"
                          placeholder={
                            !formData.transito_id ? "seleccione primero un tránsito" :
                            transitoConfig?.plate_assignment_mode === 'automatico' ? "se asignará automáticamente..." :
                            "buscar placa disponible..."
                          }
                          value={searchTerm}
                          onChange={(e) => {
                            if (transitoConfig?.plate_assignment_mode === 'automatico') return;
                            setSearchTerm(e.target.value.toUpperCase());
                            setFormData({ ...formData, placa_referencia: e.target.value.toUpperCase() });
                            setShowPlateDropdown(true);
                          }}
                          onFocus={() => {
                            if (transitoConfig?.plate_assignment_mode === 'manual' && formData.transito_id) {
                              setShowPlateDropdown(true);
                            }
                          }}
                          autoComplete="off"
                          disabled={!formData.transito_id || transitoConfig?.plate_assignment_mode === 'automatico'}
                          style={transitoConfig?.plate_assignment_mode === 'automatico' ? { background: 'var(--secondary-soft)', color: 'var(--text-secondary)', fontWeight: '700' } : {}}
                        />
                        {showPlateDropdown && transitoConfig?.plate_assignment_mode === 'manual' && formData.transito_id && (
                          <div className="searchable-dropdown">
                            {filteredPlates.length > 0 ? (
                              filteredPlates.map(p => (
                                <div key={p.id} className="dropdown-item" onClick={() => handlePlateSelect(p.plate_number)}>
                                  <span className="plate-badge-sm">{p.plate_number}</span>
                                  <small>({p.vehicle_type})</small>
                                </div>
                              ))
                            ) : (
                              <div className="dropdown-no-results">no hay placas disponibles</div>
                            )}
                          </div>
                        )}
                      </div>
                      {transitoConfig?.plate_assignment_mode === 'automatico' && (
                        <small style={{ color: 'var(--primary-color)', fontWeight: '600', marginTop: '4px', display: 'block' }}>
                          ℹ️ el sistema seleccionará la siguiente placa disponible para este tránsito.
                        </small>
                      )}
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>NÚMERO DE CHASIS</label>
                        <input 
                          type="text" 
                          name="chasis" 
                          value={formData.chasis} 
                          onChange={handleInputChange} 
                          placeholder="Número de chasis" 
                          style={formData.chasis ? { backgroundColor: '#e8f5e9', borderColor: '#4caf50' } : {}} 
                        />
                      </div>
                      <div className="form-group">
                        <label>NÚMERO DE MOTOR</label>
                        <input 
                          type="text" 
                          name="motor" 
                          value={formData.motor} 
                          onChange={handleInputChange} 
                          placeholder="Número de motor" 
                          style={formData.motor ? { backgroundColor: '#e8f5e9', borderColor: '#4caf50' } : {}} 
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>NÚMERO DE VIN</label>
                      <input 
                        type="text" 
                        name="vin" 
                        value={formData.vin} 
                        onChange={handleInputChange} 
                        placeholder="Número de VIN" 
                        style={formData.vin ? { backgroundColor: '#e8f5e9', borderColor: '#4caf50' } : {}} 
                      />
                    </div>
                  </div>

                  <div className="modal-actions" style={{ padding: '0 20px 20px 20px', marginTop: '0' }}>
                    <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>cancelar</button>
                    <button type="submit" className="btn-primary" disabled={loading} style={{ padding: '10px 25px', fontSize: '13px', fontWeight: '800' }}>
                      {loading ? '🚀 GUARDANDO...' : '🚀 GUARDAR CAMBIOS'}
                    </button>
                  </div>
                </>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default EditarTramiteModal;
